function _classPrivateFieldLooseBase(e, t) { if (!{}.hasOwnProperty.call(e, t)) throw new TypeError("attempted to use private field on non-instance"); return e; }
var id = 0;
function _classPrivateFieldLooseKey(e) { return "__private_" + id++ + "_" + e; }
import { Component } from '@theme/component';
import { isMobileBreakpoint } from '@theme/utilities';

/**
 * @typedef {Object} ShowMoreRefs
 * @property {HTMLElement} showMoreButton - The button to toggle visibility of the items
 * @property {HTMLElement[]} showMoreItems - The hidden items to show and hide
 * @property {HTMLElement} showMoreContent - The content container to measure and animate
 */

/**
 * A custom element that manages the showing and hiding excess content items
 *
 * @extends {Component<ShowMoreRefs>}
 */
var _expanded = _classPrivateFieldLooseKey("expanded");
var _disableOnDesktop = _classPrivateFieldLooseKey("disableOnDesktop");
var _collapsedHeight = _classPrivateFieldLooseKey("collapsedHeight");
var _disabledClass = _classPrivateFieldLooseKey("disabledClass");
var _currentBreakpoint = _classPrivateFieldLooseKey("currentBreakpoint");
var _animation = _classPrivateFieldLooseKey("animation");
var _animationSpeed = _classPrivateFieldLooseKey("animationSpeed");
var _updateBreakpointState = _classPrivateFieldLooseKey("updateBreakpointState");
var _expand = _classPrivateFieldLooseKey("expand");
var _collapse = _classPrivateFieldLooseKey("collapse");
var _animateHeight = _classPrivateFieldLooseKey("animateHeight");
var _onAnimationFinish = _classPrivateFieldLooseKey("onAnimationFinish");
class ShowMoreComponent extends Component {
  constructor(...args) {
    super(...args);
    /**
     * Handles the animation finish event.
     */
    Object.defineProperty(this, _onAnimationFinish, {
      value: _onAnimationFinish2
    });
    /**
     * @type {'MOBILE' | 'DESKTOP'}
     */
    Object.defineProperty(this, _currentBreakpoint, {
      get: _get_currentBreakpoint,
      set: void 0
    });
    this.requiredRefs = ['showMoreButton', 'showMoreItems', 'showMoreContent'];
    /**
     * @type {boolean}
     */
    Object.defineProperty(this, _expanded, {
      writable: true,
      value: false
    });
    /**
     * @type {boolean}
     */
    Object.defineProperty(this, _disableOnDesktop, {
      writable: true,
      value: false
    });
    /**
     * @type {number}
     */
    Object.defineProperty(this, _collapsedHeight, {
      writable: true,
      value: 0
    });
    /**
     * @type {'mobile:hidden' | 'hidden'}
     */
    Object.defineProperty(this, _disabledClass, {
      writable: true,
      value: 'hidden'
    });
    /**
     * @type {Animation | undefined}
     */
    Object.defineProperty(this, _animation, {
      writable: true,
      value: void 0
    });
    /**
     * @constant {number}
     */
    Object.defineProperty(this, _animationSpeed, {
      writable: true,
      value: 300
    });
    /**
     * Updates the current breakpoint and apprpropriate disabled class
     */
    Object.defineProperty(this, _updateBreakpointState, {
      writable: true,
      value: () => {
        _classPrivateFieldLooseBase(this, _disableOnDesktop)[_disableOnDesktop] = this.dataset.disableOnDesktop === 'true';
        _classPrivateFieldLooseBase(this, _disabledClass)[_disabledClass] = _classPrivateFieldLooseBase(this, _disableOnDesktop)[_disableOnDesktop] ? 'mobile:hidden' : 'hidden';
      }
    });
    /**
     * Handles expanding the content
     * @returns {{startHeight: number, endHeight: number}}
     */
    Object.defineProperty(this, _expand, {
      writable: true,
      value: () => {
        const {
          showMoreItems,
          showMoreContent
        } = this.refs;
        _classPrivateFieldLooseBase(this, _collapsedHeight)[_collapsedHeight] = showMoreContent.offsetHeight;
        const startHeight = _classPrivateFieldLooseBase(this, _collapsedHeight)[_collapsedHeight];
        showMoreItems?.forEach(item => item.classList.remove(_classPrivateFieldLooseBase(this, _disabledClass)[_disabledClass]));
        return {
          startHeight,
          endHeight: showMoreContent.scrollHeight
        };
      }
    });
    /**
     * Handles collapsing the content
     * @returns {{startHeight: number, endHeight: number}}
     */
    Object.defineProperty(this, _collapse, {
      writable: true,
      value: () => {
        const {
          showMoreContent
        } = this.refs;
        const startHeight = showMoreContent.offsetHeight;
        const endHeight = _classPrivateFieldLooseBase(this, _collapsedHeight)[_collapsedHeight];
        return {
          startHeight,
          endHeight
        };
      }
    });
    /**
     * Initializes a height transition
     * @param {number} startHeight
     * @param {number} endHeight
     */
    Object.defineProperty(this, _animateHeight, {
      writable: true,
      value: (startHeight, endHeight) => {
        const {
          showMoreContent
        } = this.refs;
        showMoreContent.style.overflow = 'hidden';
        _classPrivateFieldLooseBase(this, _animation)[_animation]?.cancel();
        _classPrivateFieldLooseBase(this, _animation)[_animation] = showMoreContent.animate({
          height: [`${startHeight}px`, `${endHeight}px`]
        }, {
          duration: _classPrivateFieldLooseBase(this, _animationSpeed)[_animationSpeed],
          easing: 'ease-in-out'
        });
        _classPrivateFieldLooseBase(this, _animation)[_animation].onfinish = () => _classPrivateFieldLooseBase(this, _onAnimationFinish)[_onAnimationFinish]();
      }
    });
    /**
     * Toggles the expansion state of the content.
     *
     * @param {Event} event - The click event
     */
    this.toggle = event => {
      event.preventDefault();
      _classPrivateFieldLooseBase(this, _updateBreakpointState)[_updateBreakpointState]();
      if (_classPrivateFieldLooseBase(this, _currentBreakpoint)[_currentBreakpoint] === 'DESKTOP' && _classPrivateFieldLooseBase(this, _disableOnDesktop)[_disableOnDesktop]) return;
      const {
        startHeight,
        endHeight
      } = !_classPrivateFieldLooseBase(this, _expanded)[_expanded] ? _classPrivateFieldLooseBase(this, _expand)[_expand]() : _classPrivateFieldLooseBase(this, _collapse)[_collapse]();
      this.dataset.expanded = _classPrivateFieldLooseBase(this, _expanded)[_expanded] ? 'false' : 'true';
      this.refs.showMoreButton.setAttribute('aria-expanded', this.dataset.expanded);
      _classPrivateFieldLooseBase(this, _animateHeight)[_animateHeight](startHeight, endHeight);
    };
  }
  connectedCallback() {
    super.connectedCallback();
    _classPrivateFieldLooseBase(this, _updateBreakpointState)[_updateBreakpointState]();
  }
}
function _get_currentBreakpoint() {
  return isMobileBreakpoint() ? 'MOBILE' : 'DESKTOP';
}
function _onAnimationFinish2() {
  const {
    showMoreContent,
    showMoreItems
  } = this.refs;
  if (_classPrivateFieldLooseBase(this, _expanded)[_expanded]) {
    showMoreItems.forEach(item => item.classList.add(_classPrivateFieldLooseBase(this, _disabledClass)[_disabledClass]));
  }
  showMoreContent.style.removeProperty('height');
  showMoreContent.style.overflow = '';
  _classPrivateFieldLooseBase(this, _expanded)[_expanded] = !_classPrivateFieldLooseBase(this, _expanded)[_expanded];
}
if (!customElements.get('show-more-component')) {
  customElements.define('show-more-component', ShowMoreComponent);
}
